import { SupportLayout, SUPPORT_SECTIONS } from "@/components/support-layout";
import { Link } from "react-router-dom";
import {
  LifeBuoy,
  Building2,
  Scale,
  ChevronRight,
  ExternalLink,
} from "lucide-react";

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "Support": LifeBuoy,
  "Company": Building2,
  "Legal": Scale,
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Support": "Get help, share feedback, and request API access",
  "Company": "Learn about Nordex, our team, and brand resources",
  "Legal": "Terms of service and privacy information",
};

export const SupportIndex = () => {
  return (
    <SupportLayout maxWidth="max-w-5xl">
      {/* Hero */}
      <div className="mb-12">
        <h1 className="text-3xl font-bold mb-3 tracking-tight">
          Support & Company
        </h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl">
          Get help with your Nordex account, share feedback, learn about our
          company, or review our legal policies.
        </p>
      </div>

      {/* Category Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
        {SUPPORT_SECTIONS.map((section) => {
          const Icon = CATEGORY_ICONS[section.title] || LifeBuoy;
          const description = CATEGORY_DESCRIPTIONS[section.title] || "";

          return (
            <div
              key={section.title}
              className="border border-border rounded-lg bg-card overflow-hidden group hover:border-foreground/10 transition-colors"
            >
              {/* Card Header */}
              <div className="p-5 border-b border-border/60">
                <div className="flex items-start gap-3 mb-2">
                  <div className="p-2 rounded-md bg-accent">
                    <Icon className="h-4 w-4 text-foreground/70" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold mb-1">
                      {section.title}
                    </h2>
                    {description && (
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {section.items.length}{" "}
                  {section.items.length === 1 ? "page" : "pages"}
                </div>
              </div>

              {/* Item List */}
              <div className="p-3">
                <ul className="space-y-0.5">
                  {section.items.map((item) =>
                    item.external ? (
                      <li key={item.href}>
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between py-1.5 px-2.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group/item"
                        >
                          <span>{item.label}</span>
                          <ExternalLink className="h-3 w-3 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                        </a>
                      </li>
                    ) : (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className="flex items-center justify-between py-1.5 px-2.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group/item"
                        >
                          <span>{item.label}</span>
                          <ChevronRight className="h-3 w-3 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Contact CTA */}
      <div className="p-6 border border-border rounded-lg bg-card">
        <h3 className="text-sm font-semibold mb-2">Need help?</h3>
        <p className="text-[13px] text-muted-foreground mb-4">
          Reach out to our support team for assistance with your account, trading, or any other questions.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-foreground hover:underline underline-offset-2"
        >
          Contact us
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </SupportLayout>
  );
};
